import { useState, useEffect, useCallback } from 'react';
import { apiRequest } from '../../services/api';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';
import DataTable from '../../components/DataTable';
import YilDropdown from '../../components/YilDropdown';
import UsageInfo from '../../components/UsageInfo';
import { downloadExcel, printTable } from '../../utils/exportUtils';

const AYLAR = ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'];
const INP = 'px-4 py-2.5 rounded-2xl border border-slate-200 text-sm font-bold text-slate-700 bg-white';

function fmt(n) {
  return Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' TL';
}

function Kart({ icon, label, value, sub, bg, tx }) {
  return (
    <div className={`rounded-[2.5rem] p-5 md:p-6 ${bg} border border-white/40 shadow-lg`}>
      <div className="flex items-start justify-between mb-2">
        <span className={`text-[10px] font-black uppercase tracking-[0.2em] ${tx}`}>{label}</span>
        <span className="w-10 h-10 rounded-2xl flex items-center justify-center text-xl bg-white/80 shadow-sm font-emoji">{icon}</span>
      </div>
      <div className="mt-2 text-2xl font-black text-gray-900 tracking-tighter">{value}</div>
      <p className={`text-[9px] font-black mt-3 tracking-widest uppercase opacity-80 ${tx}`}>{sub}</p>
    </div>
  );
}

export default function KdvRaporu() {
  const now = new Date();
  const gecenAy = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const [yil, setYil] = useState(gecenAy.getFullYear());
  const [ay, setAy] = useState(gecenAy.getMonth());
  const [rapor, setRapor] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState('');

  const getir = useCallback(async () => {
    setYukleniyor(true); setHata('');
    try { setRapor(await apiRequest(`Istatistik/kdv?yil=${yil}&ay=${ay + 1}`)); }
    catch (e) { setHata(e?.message || 'Rapor alınamadı.'); setRapor(null); }
    finally { setYukleniyor(false); }
  }, [yil, ay]);

  useEffect(() => { getir(); }, [getir]);

  const oranlar = rapor?.oranlar || [];
  const kaynaklar = rapor?.kaynaklar || [];
  const donem = `${AYLAR[ay]} ${yil}`;

  const BASLIK = ['KDV Oranı', 'Brüt', 'Matrah', 'Hesaplanan KDV', 'Kayıt'];
  const satirlar = () => oranlar.map(o => [`%${o.oran}`, o.brut, o.matrah, o.kdv, o.kayitSayisi]);

  return (
    <div className="p-4 md:p-6 space-y-6">
      <PageHeader
        icon="🧾" iconColor="from-teal-600 to-emerald-700" iconShadow="shadow-teal-200"
        title="KDV Raporu" subtitle="Aylık hesaplanan KDV dökümü"
      >
        <div className="flex flex-wrap gap-2">
          <button onClick={() => downloadExcel(`kdv-${yil}-${String(ay + 1).padStart(2, '0')}`, 'KDV', BASLIK, satirlar())}
            disabled={!oranlar.length}
            className="px-4 py-2 rounded-2xl bg-emerald-500 text-white text-xs font-black uppercase tracking-widest disabled:opacity-40">Excel İndir</button>
          <button onClick={() => printTable('KDV RAPORU', BASLIK, satirlar(), `${donem} — Hesaplanan KDV: ${fmt(rapor?.toplamKdv)}`)}
            disabled={!oranlar.length}
            className="px-4 py-2 rounded-2xl bg-slate-700 text-white text-xs font-black uppercase tracking-widest disabled:opacity-40">Yazdır</button>
        </div>
      </PageHeader>

      <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm p-4 md:p-5 flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Yıl</label>
          <YilDropdown value={yil} onChange={setYil} />
        </div>
        <div>
          <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Ay</label>
          <select className={INP} value={ay} onChange={e => setAy(Number(e.target.value))}>
            {AYLAR.map((a, i) => <option key={a} value={i}>{a}</option>)}
          </select>
        </div>
      </div>

      {hata && <div className="rounded-2xl bg-rose-50 border border-rose-200 text-rose-700 px-5 py-4 text-sm font-semibold">{hata}</div>}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Kart icon="🧾" label="Hesaplanan KDV" value={yukleniyor ? '…' : fmt(rapor?.toplamKdv)}
          sub="satışlar üzerinden" bg="bg-gradient-to-br from-teal-50 to-white" tx="text-teal-500" />
        <Kart icon="💰" label="Toplam Matrah" value={yukleniyor ? '…' : fmt(rapor?.toplamMatrah)}
          sub="vergiler hariç" bg="bg-gradient-to-br from-emerald-50 to-white" tx="text-emerald-500" />
        <Kart icon="🧮" label="Brüt Ciro" value={yukleniyor ? '…' : fmt(rapor?.toplamBrut)}
          sub="vergiler dahil" bg="bg-gradient-to-br from-sky-50 to-white" tx="text-sky-500" />
      </div>

      {rapor?.uyarilar?.length > 0 && (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 px-5 py-4 space-y-1">
          {rapor.uyarilar.map((u, i) => <p key={i} className="text-xs font-semibold text-amber-700">⚠️ {u}</p>)}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Oran bazlı */}
        <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">KDV Oranına Göre</span>
            <p className="text-[11px] text-slate-400 mt-0.5">Beyanname satırlarına karşılık gelir</p>
          </div>
          {yukleniyor ? <LoadingSpinner text="Hesaplanıyor…" />
            : oranlar.length === 0 ? <EmptyState icon="🧾" text="Bu ayda satış kaydı yok." />
            : (
              <DataTable
                anahtarAlan="oran"
                satirlar={oranlar}
                bosIkon="🧾"
                bosMetin="Bu ayda satış kaydı yok."
                toplamGoster
                yogun
                kolonlar={[
                  {
                    anahtar: 'oran', baslik: 'Oran', siralanir: true,
                    ciz: o => (
                      <span className="font-black text-gray-700">
                        %{o.oran}
                        <span className="text-[10px] font-bold text-gray-400 ml-2">{o.kayitSayisi} kayıt</span>
                      </span>
                    ),
                    toplam: () => 'TOPLAM',
                  },
                  {
                    anahtar: 'matrah', baslik: 'Matrah', hizala: 'sag', siralanir: true,
                    ciz: o => fmt(o.matrah),
                    toplam: () => fmt(rapor?.toplamMatrah),
                  },
                  {
                    anahtar: 'kdv', baslik: 'KDV', hizala: 'sag', siralanir: true,
                    ciz: o => <span className="font-black text-teal-600">{fmt(o.kdv)}</span>,
                    toplam: () => <span className="text-teal-700">{fmt(rapor?.toplamKdv)}</span>,
                  },
                ]}
              />
            )}
        </div>

        {/* Kaynak bazlı */}
        <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Gelir Kaynağına Göre</span>
            <p className="text-[11px] text-slate-400 mt-0.5">Kontrol ve mutabakat için</p>
          </div>
          {yukleniyor ? <LoadingSpinner text="Hesaplanıyor…" />
            : kaynaklar.length === 0 ? <EmptyState icon="📊" text="Kayıt yok." />
            : (
              <DataTable
                anahtarAlan="kaynak"
                satirlar={kaynaklar}
                bosIkon="📊"
                bosMetin="Kayıt yok."
                yogun
                kolonlar={[
                  {
                    anahtar: 'kaynak', baslik: 'Kaynak', siralanir: true,
                    ciz: k => (
                      <span className="font-bold text-gray-700">
                        {k.kaynak}
                        <span className="text-[10px] font-bold text-gray-400 ml-2">{k.kayitSayisi}</span>
                      </span>
                    ),
                  },
                  { anahtar: 'brut', baslik: 'Brüt', hizala: 'sag', siralanir: true, ciz: k => fmt(k.brut) },
                  {
                    anahtar: 'kdv', baslik: 'KDV', hizala: 'sag', siralanir: true,
                    ciz: k => <span className="font-black text-teal-600">{fmt(k.kdv)}</span>,
                  },
                ]}
              />
            )}
        </div>
      </div>

      <UsageInfo
        color="teal"
        description="Seçilen ayda satışlar üzerinden hesaplanan KDV'yi gösterir. Konaklama vergisi raporundan farkı: KDV her satışta doğar, satışın konaklamaya bağlı olup olmadığına bakılmaz."
        scenarios={[
          { title: 'Neler dahil?', content: 'Konaklama, adisyonlar (oda hesabı ve walk-in), havuz girişleri ve ek hizmetler. Dışarıdan gelen müşteriye yapılan satışlar da dahildir.' },
          { title: 'Oran nereden geliyor?', content: 'Her kayıt satış anındaki KDV oranını taşır. Ürünlerde stok kartından, hizmetlerde hizmet tanımından kopyalanır. Tanımlı değilse otel geneli oran kullanılır ve rapor bunu uyarır.' },
          { title: 'Konaklamada matrah', content: 'Oda tutarı vergi dahil saklandığı için matrah geriye doğru hesaplanır; konaklama vergisi de brütün içinden düşülür, KDV matrahına dahil edilmez.' },
          { title: 'Beyanname', content: 'Bu rapor yalnızca hesaplanan (satış) KDV\'sini verir. Ödenecek KDV için indirilecek (alış) KDV de gerekir — mali müşavirinize danışın.' },
        ]}
      />
    </div>
  );
}
